import { useI18n } from '@i18n';
import { Box, Stack, useTheme } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { Actor, StateFrom } from 'xstate';
import CenterBox from '@/components/common/CenterBox';
import GlobalSearchFooter from '@/components/global-search/GlobalSearchFooter';
import GlobalSearchHeader from '@/components/global-search/GlobalSearchHeader';
import GlobalSearchResults from '@/components/global-search/GlobalSearchResults';
import GlobalSearchSkeleton from '@/components/global-search/GlobalSearchSkeleton';
import GlobalSearchTabs from '@/components/global-search/GlobalSearchTabs';
import { globalSearchMachine } from '@/components/global-search/machines/globalSearchMachine';
import Scrollbar from '@/components/Scrollbar';

type Props = {
  state: StateFrom<typeof globalSearchMachine>;
  send: Actor<typeof globalSearchMachine>['send'];
};

export const GlobalSearchDialog = ({ state, send }: Props) => {
  const { t } = useI18n();
  const theme = useTheme();

  const isOpen = state.matches('open');
  const query = state.context.query;
  const notFound = state.matches({ open: 'no-results' });
  const isGlobalSearchLoading = state.matches({ open: 'loading' });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={() => send({ type: 'CLOSE' })}
      transitionDuration={{
        enter: theme.transitions.duration.shortest,
        exit: 0,
      }}
      id="global-search-dialog"
    >
      <GlobalSearchHeader />

      <Stack height={400}>
        <GlobalSearchTabs />
        <Scrollbar
          sx={{
            flex: 1,
            pr: 1,
            '& :focus-visible': {
              outline: 'none !important',
            },
          }}
        >
          {isGlobalSearchLoading ? <GlobalSearchSkeleton /> : null}
          {notFound ? (
            <CenterBox sx={{ height: 350 }}>{t('GlobalSearch_NotFound', { searchQuery: query })}</CenterBox>
          ) : (
            <Box sx={{ py: 3, px: 1 }}>
              <GlobalSearchResults />
            </Box>
          )}
        </Scrollbar>
      </Stack>
      <GlobalSearchFooter />
    </Dialog>
  );
};
